import { useCallback, useState } from "react";

export interface UseIncrementableInputOptions {
  /** Current numeric value (controlled). */
  readonly value: number;
  /** Called whenever the value changes — on keystroke, increment, or decrement. */
  readonly onChange: (value: number) => void;
  /** Lower bound (inclusive). Disables decrement at this value. */
  readonly min?: number | undefined;
  /** Upper bound (inclusive). Disables increment at this value. */
  readonly max?: number | undefined;
  /** Amount to add/subtract per button press. Defaults to 1. */
  readonly step?: number | undefined;
  /** Disables all interaction. */
  readonly disabled?: boolean | undefined;
}

export interface UseIncrementableInputReturn {
  /** The string to display in the TextInput. */
  readonly displayText: string;
  /** Whether the decrement button should be disabled. */
  readonly isDecrementDisabled: boolean;
  /** Whether the increment button should be disabled. */
  readonly isIncrementDisabled: boolean;
  /** Handler for TextInput onChangeText. */
  readonly handleChangeText: (text: string) => void;
  /** Handler for TextInput onBlur — resets display to canonical format. */
  readonly handleBlur: () => void;
  /** Handler for the increment (+) button. */
  readonly handleIncrement: () => void;
  /** Handler for the decrement (−) button. */
  readonly handleDecrement: () => void;
}

export function useIncrementableInput({
  value,
  onChange,
  min,
  max,
  step = 1,
  disabled = false,
}: UseIncrementableInputOptions): UseIncrementableInputReturn {
  const [localText, setLocalText] = useState<string | null>(null);

  const isDecrementDisabled = disabled || (min != null && value <= min);
  const isIncrementDisabled = disabled || (max != null && value >= max);

  const handleIncrement = useCallback(() => {
    const base = localText != null ? parseInt(localText, 10) || 0 : value;
    const next = base + step;
    onChange(max != null ? Math.min(next, max) : next);
    setLocalText(null);
  }, [localText, max, onChange, step, value]);

  const handleDecrement = useCallback(() => {
    const base = localText != null ? parseInt(localText, 10) || 0 : value;
    const next = base - step;
    onChange(min != null ? Math.max(next, min) : next);
    setLocalText(null);
  }, [localText, min, onChange, step, value]);

  const handleChangeText = useCallback(
    (text: string) => {
      const digitsOnly = text.replace(/[^0-9]/g, "");
      setLocalText(digitsOnly);
      onChange(parseInt(digitsOnly, 10) || 0);
    },
    [onChange],
  );

  const handleBlur = useCallback(() => {
    setLocalText(null);
  }, []);

  const displayText = localText ?? value.toString();

  return {
    displayText,
    isDecrementDisabled,
    isIncrementDisabled,
    handleChangeText,
    handleBlur,
    handleIncrement,
    handleDecrement,
  };
}
